import { AnalyticsSender, Offer } from '@funtech-int/yango-shared';
import { MovieCard, SelectionMovie } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import {
    serialStructureMovieImpressionNavigated,
    serialStructureShowed,
    serialStructureSubscriptionOfferNavigatedV5,
    serialStructureSubscriptionOfferSucceedV4,
} from '@kinopoisk-int/evgen-web-events/web/serial_structure';
import {
    movieCardErrorRaised,
    movieCardMovieCardImpressionMarked,
    movieCardMovieCardImpressionNavigatedV3,
    MovieCardMovieCardImpressionNavigatedV3To,
    movieCardMovieCardImpressionShowedV2,
    movieCardMovieImpressionWithCardSelectedV2,
    movieCardMovieImpressionWithCardShowedV2,
    movieCardSelectionNavigated,
    MovieCardSelectionNavigatedTo,
    movieCardShowed,
    movieCardSubscriptionOfferNavigatedV6,
    movieCardSubscriptionOfferShowedV6,
    movieCardSubscriptionOfferSucceedV6,
} from '@kinopoisk-int/evgen-web-events/web/movie_card';
import {
    ContentMarkType,
    ErrorType,
    MovieCardSubscriptionNavigationTo,
    MovieCardTabTitle,
    MovieOfferEntityType,
    MovieType,
    PaymentTypes,
    SelectionEntryV2,
    SubscriptionsOfferEntityType,
} from '@kinopoisk-int/evgen-web-events/web/named_enums';

import { checkIsTvSeries } from '@/shared/lib/movie';
import { normalizeItemPosition } from '@/shared/lib/analytics/selection';
import { getBadgeType } from '@/shared/lib/analyticsBranded';
import { ProfileManager } from '@/shared/lib/profileManager';
import { getOfferTitle } from '@/shared/lib/analytics';
import { OfferAnalytics } from '@/shared/lib/analytics/offer';

import { MovieOverviewSelection } from '../hooks/useMovieOverviewQuery';

interface MovieContentActionsContentAnalytics {
    movieImpressionNavigated(this: void, to: 'payment' | 'player' | 'trailer_player'): void;
    movieImpressionMarked(
        this: void,
        type:
            | 'bookmarked'
            | 'unbookmarked'
            | 'markedAsNotInteresting'
            | 'unmarkedAsNotInteresting'
            | 'watched'
            | 'unwatched'
    ): void;
}

export type MovieContentActionsAnalytics = MovieContentActionsContentAnalytics & OfferAnalytics;

interface SelectionAnalytics {
    cardShowed(
        selectionPosition: number,
        movie: SelectionMovie,
        position: number,
        profileManager: ProfileManager
    ): void;
    cardClicked(
        selectionPosition: number,
        movie: SelectionMovie,
        position: number,
        profileManager: ProfileManager
    ): void;
    selectionNavigated(selectionPosition: number): void;
}

interface SerialStructureContentAnalytics {
    serialStructureShowed(this: void): void;
    episodeClicked(this: void, seasonNumber: number, seriesTitle: string): void;
}

interface SerialStructureOfferAnalytics {
    navigated(this: void, title: string, offer: Offer): void;
    succeeded(this: void, title: string, offer: Offer): void;
}

export type SerialStructureAnalytics = SerialStructureContentAnalytics &
    SerialStructureOfferAnalytics;

export interface MovieCardAnalytics {
    movieCardShowed(this: void, contentId: string): void;
    movieCardImpressionShowed(this: void, contentId: string, title: string): void;
    movieCardErrorRaised(this: void, contentId: string): void;
    createSerialStructureAnalytics(contentId: string, movieTitle: string): SerialStructureAnalytics;
    createMovieContentActionsAnalytics(movie: MovieCard): MovieContentActionsAnalytics;
    createSelectionAnalytics(selection: MovieOverviewSelection): SelectionAnalytics;
}

export function createMovieCardAnalytics(eventSender: AnalyticsSender): MovieCardAnalytics {
    function createMovieOfferAnalytics(movie: MovieCard): OfferAnalytics {
        return {
            showed(offer) {
                const { tariffOffer, optionOffers = [] } = offer.catalogCompositeOffer || {};
                const firstOptionOffer = optionOffers?.[0];

                eventSender.sendOnce(movieCardSubscriptionOfferShowedV6, {
                    entityType: MovieOfferEntityType.PlayButton,
                    offerTitle: getOfferTitle(offer),
                    buttonText: offer.offerTexts.buttonText,
                    billingProductId: 'tarifficator',
                    billingProductIds: offer.billingProductIds,
                    offerOptionNames: firstOptionOffer?.option?.name ?? '',
                    offerTariffName: tariffOffer?.tariff?.name ?? '',
                    position: 1,
                    title: movie.title.toString(),
                    uuid: movie.contentId ?? '-',
                    show: offer.offerPositionsData,
                });
            },

            navigated(offer) {
                const { tariffOffer, optionOffers = [] } = offer.catalogCompositeOffer || {};
                const firstOptionOffer = optionOffers?.[0];

                eventSender.send(movieCardSubscriptionOfferNavigatedV6, {
                    entityType: MovieOfferEntityType.PlayButton,
                    offerTitle: getOfferTitle(offer),
                    buttonText: offer.offerTexts.buttonText,
                    billingProductId: 'tarifficator',
                    billingProductIds: offer.billingProductIds,
                    offerOptionNames: firstOptionOffer?.option?.name ?? '',
                    offerTariffName: tariffOffer?.tariff?.name ?? '',
                    position: 1,
                    title: movie.title.toString(),
                    uuid: movie.contentId ?? '-',
                    click: offer.offerPositionsData,
                    to: MovieCardSubscriptionNavigationTo.Payment,
                });
            },

            succeeded(offer) {
                const { tariffOffer, optionOffers = [] } = offer.catalogCompositeOffer || {};
                const firstOptionOffer = optionOffers?.[0];

                eventSender.send(movieCardSubscriptionOfferSucceedV6, {
                    entityType: MovieOfferEntityType.PlayButton,
                    offerTitle: getOfferTitle(offer),
                    buttonText: offer.offerTexts.buttonText,
                    billingProductIds: offer.billingProductIds,
                    offerOptionNames: firstOptionOffer?.option?.name ?? '',
                    offerTariffName: tariffOffer?.tariff?.name ?? '',
                    paymentType: PaymentTypes.PaymentWidget,
                    position: 1,
                    title: movie.title.toString(),
                    uuid: movie.contentId ?? '-',
                });
            },
        };
    }

    function createSerialStructureOfferAnalytics(contentId: string): SerialStructureOfferAnalytics {
        const getOfferEventData = (offer: Offer, title: string) => {
            const catalogCompositeOffer = offer?.catalogCompositeOffer;
            const tariffOffer = catalogCompositeOffer?.tariffOffer;
            const optionOffers = catalogCompositeOffer?.optionOffers || [];
            const firstOptionOffer = optionOffers?.[0];

            return {
                entityType: SubscriptionsOfferEntityType.OfferIcon,
                offerTitle: getOfferTitle(offer),
                buttonText: '',
                billingProductId: 'tarifficator',
                billingProductIds: offer.billingProductIds,
                offerOptionNames: firstOptionOffer?.option?.name ?? '',
                offerTariffName: tariffOffer?.tariff?.name ?? '',
                position: 1,
                title,
                uuid: contentId ?? '-',
            };
        };

        return {
            navigated(title, offer) {
                const eventData = getOfferEventData(offer, title);

                eventSender.send(serialStructureSubscriptionOfferNavigatedV5, {
                    ...eventData,
                    click: offer.offerPositionsData,
                });
            },

            succeeded(title, offer) {
                const eventData = getOfferEventData(offer, title);

                eventSender.send(serialStructureSubscriptionOfferSucceedV4, eventData);
            },
        };
    }

    return {
        movieCardShowed(contentId) {
            eventSender.sendOnce(movieCardShowed, {
                uuid: contentId,
            });
        },
        movieCardImpressionShowed(contentId, title) {
            eventSender.sendOnce(movieCardMovieCardImpressionShowedV2, {
                uuid: contentId ?? '-',
                title: title ?? '-',
            });
        },
        movieCardErrorRaised(contentId) {
            eventSender.sendOnce(movieCardErrorRaised, {
                uuid: contentId ?? '-',
                errorType: ErrorType.AppError,
                errorTitle: '404',
                errorMessage: 'Not found',
                requestId: '-',
            });
        },
        createMovieContentActionsAnalytics(movie: MovieCard) {
            return {
                movieImpressionNavigated(to) {
                    const navigatedTo = (() => {
                        switch (to) {
                            case 'trailer_player':
                                return MovieCardMovieCardImpressionNavigatedV3To.TrailerPlayerScreen;
                            case 'player':
                                return MovieCardMovieCardImpressionNavigatedV3To.PlayerScreen;
                            case 'payment':
                                return MovieCardMovieCardImpressionNavigatedV3To.Payment;
                            default:
                                throw new Error(`Unknown to: ${to}`);
                        }
                    })();

                    eventSender.send(movieCardMovieCardImpressionNavigatedV3, {
                        uuid: movie.contentId ?? '-',
                        title: movie.title.toString() ?? '-',
                        movieType: checkIsTvSeries(movie.typename)
                            ? MovieType.TvSeries
                            : MovieType.Film,
                        availableOnline: true,
                        to: navigatedTo,
                    });
                },

                movieImpressionMarked(type) {
                    const markType = (() => {
                        switch (type) {
                            case 'bookmarked':
                                return ContentMarkType.Bookmarked;
                            case 'unbookmarked':
                                return ContentMarkType.Unbookmarked;
                            case 'markedAsNotInteresting':
                                return ContentMarkType.MarkedAsNotInteresting;
                            case 'unmarkedAsNotInteresting':
                                return ContentMarkType.UnmarkedAsNotInteresting;
                            case 'watched':
                                return ContentMarkType.MarkedAsSeen;
                            case 'unwatched':
                                return ContentMarkType.UnmarkedAsSeen;
                            default:
                                throw new Error(`Unknown type: ${type}`);
                        }
                    })();

                    eventSender.send(movieCardMovieCardImpressionMarked, {
                        uuid: movie.contentId ?? '-',
                        title: movie.title.toString() ?? '-',
                        markType,
                    });
                },

                ...createMovieOfferAnalytics(movie),
            };
        },
        createSelectionAnalytics(selection: MovieOverviewSelection) {
            const entityType = 'Director';
            return {
                cardShowed(selectionPosition, movie, position, profileManager) {
                    const badgeType = getBadgeType({
                        profileManager,
                        isPurchasable: Boolean(movie.viewOption?.isPurchasable),
                    });

                    eventSender.sendOnce(movieCardMovieImpressionWithCardShowedV2, {
                        uuid: movie.contentId ?? '-',
                        title: movie.title.toString() ?? '-',
                        badgeType,
                        cardPosition: normalizeItemPosition(position),
                        selectionId: selection.id,
                        selectionName: selection.title ?? '-',
                        selectionPosition: normalizeItemPosition(selectionPosition),
                        entityType,
                        windowId: selection.showcaseId ?? '-',
                        windowSessionId: selection.showcaseId ?? '-',
                    });
                },
                cardClicked(selectionPosition, movie, position, profileManager) {
                    const badgeType = getBadgeType({
                        profileManager,
                        isPurchasable: Boolean(movie.viewOption?.isPurchasable),
                    });

                    eventSender.sendOnce(movieCardMovieImpressionWithCardSelectedV2, {
                        uuid: movie.contentId ?? '-',
                        title: movie.title.toString() ?? '-',
                        badgeType,
                        cardPosition: normalizeItemPosition(position),
                        selectionId: selection.id,
                        selectionName: selection.title ?? '-',
                        selectionPosition: normalizeItemPosition(selectionPosition),
                        entityType,
                        windowId: selection.showcaseId ?? '-',
                        windowSessionId: selection.showcaseId ?? '-',
                        tabTitle: MovieCardTabTitle.None,
                    });
                },
                selectionNavigated(selectionPosition) {
                    eventSender.send(movieCardSelectionNavigated, {
                        entityType: SelectionEntryV2.SelectionHeader,
                        selectionId: selection.id,
                        selectionName: selection.title ?? '-',
                        selectionPosition: normalizeItemPosition(selectionPosition),
                        windowSessionId: selection.showcaseId ?? '-',
                        to: MovieCardSelectionNavigatedTo.SelectionScreen,
                    });
                },
            };
        },
        createSerialStructureAnalytics(contentId, movieTitle) {
            return {
                serialStructureShowed() {
                    eventSender.sendOnce(serialStructureShowed, {
                        uuid: contentId ?? '-',
                        movieTitle: movieTitle ?? '-',
                    });
                },

                episodeClicked(seasonNumber, seriesTitle) {
                    const selectionName =
                        seriesTitle && seasonNumber ? `${seriesTitle}_season_${seasonNumber}` : '-';

                    eventSender.send(serialStructureMovieImpressionNavigated, {
                        uuid: contentId ?? '-',
                        title: seriesTitle ?? '-',
                        selectionName: selectionName ?? '-',
                    });
                },

                ...createSerialStructureOfferAnalytics(contentId),
            };
        },
    };
}
